import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { TeamBadge } from "@/components/TeamBadge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Save, History, Activity, Wand2, RotateCcw, Flame, Clock, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

function FormPills({ side }) {
  if (!side) return null;
  const color = (o) => o === "W" ? "tok-win"
    : o === "L" ? "tok-loss" : "tok-draw";
  return (
    <div className="flex items-center gap-2" data-testid={`form-team-${side.team?.short}`}>
      <TeamBadge team={side.team} size={22} />
      <span className="text-sm font-medium w-24 truncate">{side.team?.name}</span>
      <div className="flex gap-1">
        {side.form.map((o, i) => (
          <span key={i} className={`w-5 h-5 grid place-items-center text-[10px] font-data font-bold ${color(o)}`}>{o}</span>
        ))}
      </div>
    </div>
  );
}

function WinProbBar({ prob, fid, t }) {
  if (!prob) return null;
  const seg = [
    { k: "H", v: prob.home, cls: "bg-primary" },
    { k: "D", v: prob.draw, cls: "bg-muted-foreground/40" },
    { k: "A", v: prob.away, cls: "bg-[hsl(var(--stadium-yellow))]" },
  ];
  return (
    <div className="mt-3" data-testid={`win-prob-${fid}`}>
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-data">
        <span className="flex items-center gap-1.5">
          {t("prob.title")}
          {prob.source === "market" ? (
            <span className="inline-flex items-center gap-0.5 text-[hsl(var(--pitch-green))]" title={prob.bookmaker ? `Powered by live bookmaker odds · ${prob.bookmaker}` : "Powered by live bookmaker odds"} data-testid={`prob-src-${fid}`}>· Market odds <Info className="w-3 h-3 opacity-70" /></span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-muted-foreground/70" title="Model estimate — real bookmaker odds appear closer to kickoff" data-testid={`prob-src-${fid}`}>· Estimate <Info className="w-3 h-3 opacity-70" /></span>
          )}
        </span>
        <span><span className="text-primary">{prob.home}%</span> · {prob.draw}% · <span className="text-[hsl(var(--stadium-yellow))]">{prob.away}%</span></span>
      </div>
      <div className="flex h-2.5 overflow-hidden border border-border">
        {seg.map((s) => <div key={s.k} className={s.cls} style={{ width: `${s.v}%` }} title={`${s.k} ${s.v}%`} />)}
      </div>
    </div>
  );
}

function KickoffCountdown({ date, fid, finished }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  if (finished || !date) return null;
  const ts = new Date(date).getTime();
  if (isNaN(ts)) return null;
  const diff = ts - now;
  let label, live = false;
  if (diff <= 0) {
    if (diff > -2.5 * 3600 * 1000) { label = "Kicking off"; live = true; }
    else label = "Kicked off";
  } else {
    const mins = Math.floor(diff / 60000);
    const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
    if (d > 0) label = `in ${d}d ${h}h`;
    else if (h > 0) label = `in ${h}h ${m}m`;
    else label = `in ${m}m`;
  }
  const dt = new Date(date);
  const when = dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
    + " · " + dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return (
    <p className="flex items-center justify-center gap-1.5 mt-2 text-[11px] font-data uppercase tracking-wider text-muted-foreground"
      data-testid={`kickoff-${fid}`}>
      <Clock size={11} className={live ? "text-primary animate-pulse" : ""} />
      <span>{when}</span>
      <span className={live ? "text-primary font-bold" : "text-foreground/80"}>· {label}</span>
    </p>
  );
}


export function FixturePredictor({ leagueId, initialMatchday, highlightTeamId, onPredicted, onWeekComplete, isPremium }) {
  const { t } = useI18n();
  const [fixtures, setFixtures] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [md, setMd] = useState(initialMatchday || 1);
  const [preds, setPreds] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({}); // fixtureId -> {open, data, loading}
  const [probs, setProbs] = useState({}); // fixtureId -> {home,draw,away}

  useEffect(() => {
    if (!leagueId) return;
    api.get(`/leagues/${leagueId}/suggested`, { params: { matchday: md } })
      .then(({ data }) => {
        const m = {};
        Object.entries(data.suggestions).forEach(([fid, s]) => { m[fid] = { ...s.prob, source: s.prob_source, bookmaker: s.bookmaker }; });
        setProbs(m);
      }).catch(() => {});
  }, [leagueId, md]);

  const toggleForm = async (fid) => {
    const cur = form[fid];
    if (cur?.open) { setForm((s) => ({ ...s, [fid]: { ...cur, open: false } })); return; }
    if (cur?.data) { setForm((s) => ({ ...s, [fid]: { ...cur, open: true } })); return; }
    setForm((s) => ({ ...s, [fid]: { open: true, loading: true } }));
    try {
      const { data } = await api.get(`/fixtures/${fid}/form`);
      setForm((s) => ({ ...s, [fid]: { open: true, loading: false, data } }));
    } catch {
      setForm((s) => ({ ...s, [fid]: { open: false, loading: false } }));
    }
  };
  const [h2h, setH2h] = useState({}); // fixtureId -> {open, data, loading}

  const loadH2h = async (fid, scope) => {
    setH2h((s) => ({ ...s, [fid]: { ...(s[fid] || {}), open: true, loading: true, scope } }));
    try {
      const { data } = await api.get(`/fixtures/${fid}/h2h`, { params: { scope } });
      setH2h((s) => ({ ...s, [fid]: { open: true, loading: false, data, scope, cache: { ...(s[fid]?.cache || {}), [scope]: data } } }));
    } catch {
      setH2h((s) => ({ ...s, [fid]: { ...(s[fid] || {}), open: false, loading: false } }));
    }
  };

  const setH2hScope = async (fid, scope) => {
    const cur = h2h[fid];
    if (cur?.scope === scope) return;
    if (cur?.cache?.[scope]) {
      setH2h((s) => ({ ...s, [fid]: { ...cur, open: true, data: cur.cache[scope], scope } }));
      return;
    }
    await loadH2h(fid, scope);
  };

  const toggleH2h = async (fid) => {
    const cur = h2h[fid];
    if (cur?.open) {
      setH2h((s) => ({ ...s, [fid]: { ...cur, open: false } }));
      return;
    }
    if (cur?.data) {
      setH2h((s) => ({ ...s, [fid]: { ...cur, open: true } }));
      return;
    }
    await loadH2h(fid, "venue");
  };

  const load = async () => {
    const [fx, mp] = await Promise.all([
      api.get(`/leagues/${leagueId}/fixtures`),
      api.get(`/leagues/${leagueId}/my-fixture-predictions`),
    ]);
    setFixtures(fx.data.fixtures);
    setMatchdays(fx.data.matchdays);
    setPreds(mp.data.predictions || {});
  };

  useEffect(() => { load(); }, [leagueId]);
  useEffect(() => { if (initialMatchday) setMd(initialMatchday); }, [initialMatchday]);

  const current = useMemo(() => fixtures.filter((f) => f.matchday === md), [fixtures, md]);

  const setScore = (fid, side, val) => {
    const v = val === "" ? "" : Math.max(0, Math.min(30, parseInt(val) || 0));
    setPreds((p) => ({ ...p, [fid]: { ...(p[fid] || { home_score: "", away_score: "" }), [side]: v } }));
  };

  const suggest = async (fid) => {
    try {
      const { data } = await api.get(`/fixtures/${fid}/suggested`);
      const { home_score, away_score } = data.suggested;
      setPreds((p) => ({ ...p, [fid]: { home_score, away_score } }));
      toast.success(t("suggest.toast", { h: home_score, a: away_score }),
        { description: data.based_on_ratings ? undefined : t("suggest.rate") });
    } catch {
      toast.error(t("scores.saveErr"));
    }
  };

  const suggestAll = async () => {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/suggested`, { params: { matchday: md } });
      setPreds((p) => ({ ...p, ...data.suggestions }));
      toast.success(t("suggest.allToast", { n: Object.keys(data.suggestions).length }));
    } catch {
      toast.error(t("scores.saveErr"));
    }
  };

  const suggestSeason = async () => {
    try {
      const { data } = await api.post(`/leagues/${leagueId}/suggest-season`);
      await load();
      toast.success(t("suggest.seasonToast", { n: data.count }));
    } catch {
      toast.error(t("scores.saveErr"));
    }
  };

  const resetSeason = async () => {
    try {
      const { data } = await api.delete(`/leagues/${leagueId}/my-fixture-predictions`);
      setPreds({});
      toast.success(t("reset.toast", { n: data.deleted }));
    } catch {
      toast.error(t("scores.saveErr"));
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const toSave = current.filter((f) => {
        const p = preds[f.id];
        return p && p.home_score !== "" && p.away_score !== "";
      });
      await Promise.all(
        toSave.map((f) =>
          api.put(`/fixtures/${f.id}/prediction`, {
            home_score: preds[f.id].home_score, away_score: preds[f.id].away_score,
          })
        )
      );
      toast.success(t("scores.saved", { n: toSave.length, count: toSave.length }));
      if (toSave.length > 0 && onPredicted) onPredicted();
      const weekComplete = current.length > 0 && current.every((f) => {
        const p = preds[f.id];
        return p && p.home_score !== "" && p.home_score != null && p.away_score !== "" && p.away_score != null;
      });
      if (weekComplete && onWeekComplete) onWeekComplete(md);
    } catch {
      toast.error(t("scores.saveErr"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <p className="overline text-primary">{t("scores.overline")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("scores.weekOf", { a: md, b: matchdays.length })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={suggestSeason} variant="outline" data-testid="suggest-season-btn"
            className="font-bold border-primary/50 text-primary hover:bg-primary/10">
            <Wand2 size={16} className="mr-2" /> {t("suggest.season")}
          </Button>
          <Button onClick={suggestAll} variant="outline" data-testid="suggest-all-btn"
            className="font-bold border-primary/50 text-primary hover:bg-primary/10">
            <Wand2 size={16} className="mr-2" /> {t("suggest.all")}
          </Button>
          <Button onClick={saveAll} disabled={saving} data-testid="save-fixtures-btn"
            className="bg-primary text-primary-foreground font-bold hover:brightness-110">
            <Save size={16} className="mr-2" /> {saving ? t("common.saving") : t("scores.saveWeek")}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" data-testid="reset-btn" className="text-muted-foreground hover:text-destructive">
                <RotateCcw size={16} className="mr-2" /> {t("reset.button")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("reset.title")}</AlertDialogTitle>
                <AlertDialogDescription>{t("reset.desc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel-btn">{t("reset.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={resetSeason} data-testid="reset-confirm-btn"
                  className="bg-destructive text-destructive-foreground hover:brightness-110">
                  {t("reset.confirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-4" data-testid="matchday-selector">
        {matchdays.map((m) => (
          <button key={m} onClick={() => setMd(m)} data-testid={`matchday-${m}`}
            className={`shrink-0 min-w-12 px-2 h-12 border-2 font-data font-bold text-base transition-colors ${
              m === md ? "border-primary text-primary-foreground bg-primary" : "border-border text-muted-foreground hover:border-primary"
            }`}>
            W{m}
          </button>
        ))}
      </div>

      <div className="px-1 mb-2">
        <p className="overline text-muted-foreground" data-testid="week-heading">{t("scores.weekFixtures", { n: md })}</p>
      </div>

      <div className="bg-card border-2 border-foreground" data-testid="fixtures-list">
        {current.map((f) => {
          const p = preds[f.id] || {};
          const finished = f.status === "FINISHED";
          const mine = highlightTeamId && (f.home?.id === highlightTeamId || f.away?.id === highlightTeamId);
          return (
            <div key={f.id} className={`border-t border-border first:border-t-0 px-4 py-4 ${mine ? "bg-primary/10 ring-2 ring-primary ring-inset" : ""}`} data-testid={`fixture-${f.id}`}>
              {mine && <p className="text-center overline text-primary mb-2" data-testid={`your-club-tag-${f.id}`}>★ Your club</p>}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                  <span className="font-medium truncate text-right">{f.home?.name}</span>
                  <TeamBadge team={f.home} size={26} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input type="number" min="0" max="30" value={p.home_score ?? ""} onChange={(e) => setScore(f.id, "home_score", e.target.value)}
                    data-testid={`home-score-${f.id}`}
                    className="w-14 h-14 text-center bg-background border-2 border-border font-data font-bold text-2xl focus:border-primary focus:outline-none focus:bg-primary/10 transition-colors" />
                  <span className="text-muted-foreground font-data text-lg">:</span>
                  <input type="number" min="0" max="30" value={p.away_score ?? ""} onChange={(e) => setScore(f.id, "away_score", e.target.value)}
                    data-testid={`away-score-${f.id}`}
                    className="w-14 h-14 text-center bg-background border-2 border-border font-data font-bold text-2xl focus:border-primary focus:outline-none focus:bg-primary/10 transition-colors" />
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <TeamBadge team={f.away} size={26} />
                  <span className="font-medium truncate">{f.away?.name}</span>
                </div>
              </div>
              <KickoffCountdown date={f.date} fid={f.id} finished={finished} />
              {finished && (
                <p className="text-center text-xs text-muted-foreground mt-2" data-testid={`actual-${f.id}`}>
                  {t("scores.actual")} <span className="text-foreground font-medium">{f.home_score} - {f.away_score}</span>
                </p>
              )}
              {isPremium
                ? <WinProbBar prob={probs[f.id]} fid={f.id} t={t} />
                : (probs[f.id] && (
                    <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-data uppercase tracking-wider text-[hsl(var(--stadium-yellow))]/80" data-testid={`win-prob-locked-${f.id}`}>
                      <Lock size={11} /> Win probability · Premium
                    </p>
                  ))}
              <div className="flex justify-center gap-4 mt-2">
                <button onClick={() => toggleH2h(f.id)} data-testid={`h2h-btn-${f.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <History size={13} /> {t("h2h.button")}
                </button>
                <button onClick={() => toggleForm(f.id)} data-testid={`form-btn-${f.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Activity size={13} /> {t("form.button")}
                </button>
                <button onClick={() => suggest(f.id)} data-testid={`suggest-btn-${f.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:brightness-110 transition-colors">
                  <Wand2 size={13} /> {t("suggest.button")}
                </button>
              </div>
              {form[f.id]?.open && (
                <div className="mt-3 bg-secondary/40 border border-border p-3" data-testid={`form-panel-${f.id}`}>
                  {form[f.id].loading ? (
                    <p className="text-center text-xs text-muted-foreground">…</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="overline text-primary">{t("form.title")}</p>
                        {form[f.id].data.home.is_sample && form[f.id].data.away.is_sample && (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5">{t("h2h.sample")}</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FormPills side={form[f.id].data.home} />
                        <FormPills side={form[f.id].data.away} />
                      </div>
                    </>
                  )}
                </div>
              )}
              {h2h[f.id]?.open && (
                <div className="mt-3 bg-secondary/40 border border-border p-3" data-testid={`h2h-panel-${f.id}`}>
                  {h2h[f.id].loading ? (
                    <p className="text-center text-xs text-muted-foreground">…</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="overline text-primary">{t("h2h.title")}</p>
                        <div className="flex items-center border border-border" data-testid={`h2h-scope-toggle-${f.id}`}>
                          {["venue", "all"].map((sc) => {
                            const locked = sc === "all" && !isPremium;
                            return (
                            <button
                              key={sc}
                              onClick={() => locked
                                ? toast.info("\"Any venue\" H2H is a Premium feature", { description: "Go Premium to see the full rivalry across both grounds." })
                                : setH2hScope(f.id, sc)}
                              data-testid={`h2h-scope-${sc}-${f.id}`}
                              className={`flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 transition-colors ${
                                (h2h[f.id].scope || "venue") === sc
                                  ? "bg-primary text-primary-foreground"
                                  : locked ? "text-[hsl(var(--stadium-yellow))]/70" : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {locked && <Lock size={9} />}
                              {sc === "venue" ? "This fixture" : "Any venue"}
                            </button>
                            );
                          })}
                        </div>
                      </div>
                      {h2h[f.id].data.played > 0 && (() => {
                        const s = h2h[f.id].data.summary || {};
                        const hw = s.home_wins || 0, aw = s.away_wins || 0, dr = s.draws || 0;
                        const drawTxt = dr > 0 ? ` (${dr} drawn)` : "";
                        let line;
                        if (hw > aw) line = <><span className="text-[hsl(var(--pitch-green))]">{f.home?.name || f.home?.short} lead {hw}-{aw}</span> in recent meetings{drawTxt}</>;
                        else if (aw > hw) line = <><span className="text-[hsl(var(--pitch-green))]">{f.away?.name || f.away?.short} lead {aw}-{hw}</span> in recent meetings{drawTxt}</>;
                        else line = <>Honours even — <span className="text-primary">{hw}-{aw}{drawTxt.replace(" (", ", ").replace(")", "")}</span> in recent meetings</>;
                        return (
                          <p className="mb-2 font-display text-sm uppercase tracking-wide" data-testid={`h2h-record-${f.id}`}>{line}</p>
                        );
                      })()}
                      {h2h[f.id].data.streak && (
                        <p className="mb-2 -mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-2 py-0.5" data-testid={`h2h-streak-${f.id}`}>
                          <Flame size={12} /> {h2h[f.id].data.streak.text}
                        </p>
                      )}
                      <div className="space-y-1">
                        {h2h[f.id].data.results.map((r, ri) => {
                          const hs = r.home_score, as_ = r.away_score;
                          const cls = r.no_data
                            ? "text-muted-foreground/40"
                            : hs > as_ ? "text-[hsl(var(--pitch-green))]" : hs < as_ ? "text-destructive" : "text-muted-foreground";
                          const showVenue = h2h[f.id].scope === "all" && !r.no_data;
                          return (
                            <div key={ri} className="flex items-center justify-between text-sm" data-testid={`h2h-row-${f.id}-${ri}`}>
                              <span className="text-muted-foreground text-xs w-16 font-data">
                                {r.season}
                                {showVenue && (
                                  <span className="ml-1 text-[9px] text-muted-foreground/60">{r.venue}</span>
                                )}
                              </span>
                              <span className={`flex-1 text-end truncate ${r.no_data ? "text-muted-foreground/50" : ""}`}>{f.home?.short}</span>
                              {r.no_data ? (
                                <span className="font-data text-[10px] uppercase tracking-wider w-14 text-center text-muted-foreground/50">n/a</span>
                              ) : (
                                <span className={`font-data font-bold text-base w-14 text-center ${cls}`}>{hs} - {as_}</span>
                              )}
                              <span className={`flex-1 truncate ${r.no_data ? "text-muted-foreground/50" : ""}`}>{f.away?.short}</span>
                            </div>
                          );
                        })}
                      </div>
                      {h2h[f.id].data.played === 0 && (
                        <p className="mt-2 text-center font-data text-[10px] uppercase tracking-wider text-muted-foreground/70">Not met in the last 5 seasons</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
